
import { Failure, Fetcher } from 'query/Fetch';
import { AndClause, ClauseDispatcher, ColumnClause, LiteralClause, OperatorClause, OrClause, OrdOperator, RootClause, SearchClause } from 'query/Filter';
import { QueryColumn } from 'query/QueryColumn';
import { Row } from 'table/Row';
import Query from '../query/Query';
import Table from '../table/Table';
import { toFullURL } from './OData';
import { Cell, CellType } from 'table/Cell';
import MultipleQueryColumn, { OrderedBy } from 'query/MultipleQueryColumn';
import { ColumnType } from 'query/ColumnType';
import { SingleQueryColumn } from 'query/SingleQueryColumn';

export class ODataTable extends Table {
    private _baseURL: string;

    constructor(baseURL: string, name: string) {
        super();
        this._baseURL = baseURL;
        this.name = name;
    }

    /* The OData URL conventions standard that this is based on:
    http://docs.oasis-open.org/odata/odata/v4.01/odata-v4.01-part2-url-conventions.html
    */

    /* Return the URL used to find how many rows are in the table. */
    private countUrl: (q: Query) => URL = (q) => {
        let url = this.createUrl(q);
        this.addCount(url, q);
        return url;
    }

    /* Return the URL used to retrieve the rows from a table */
    private rowsUrl: (q: Query, from: number, to: number) => URL = (q, from, to) => {
        let url = this.createUrl(q);
        this.addSelect(url, q);
        url.searchParams.set("$top", Math.round(to - from).toString());
        url.searchParams.set("$skip", Math.floor(from).toString());
        this.addFilter(url, q);
        this.addOrderBy(url, q);
        return url;
    }

    /* Used by a UI to copy the URL to the clipboard. OData specific. */
    public getUrl = (q: Query) => {
        let url = this.createUrl(q);
        this.addSelect(url, q);
        this.addFilter(url, q);
        this.addOrderBy(url, q);
        return url;
    }

    /* For testing only. Do not use. */
    public testRowsUrl: (q: Query, from: number, to: number) => URL = (q, from, to) => {
        return this.rowsUrl(q, from, to);
    }

    private createUrl: (q: Query) => URL = (q) => {
        let base = this._baseURL + this.name;
        let result = new URL(toFullURL(base));
        return result;
    }

    private addCount: (url: URL, q: Query) => void = (url, q) => {
        url.searchParams.append("$count", "true");

        // We don't want results. We want to do two separate
        // queries to get the count and then the rows.
        url.searchParams.append("$top", "0");
    }

    /* Add the selects and expands.
       This is complicated a bit by the root level selects and expands
       being separated by & (i.e. they're URL parameters), but inner
       selects and expands are semicolon separated.

       I tried using "$select=columnName/fkColumn,..." instead, but although
       these appear in the OData spec, it seems that they're not
       implemented by RESTier. It looks like we need to use embedded expands, 
       e.g. "$expand=fkColumn($select=fkColumns...;$expand=...)
    */
    private addSelect: (url: URL, q: Query) => void = (url, q) => {

        // Beware of parameter injection as a security risk (TODO)
        // I guess this means stripping out ampersands and semicolons?

        if (q.columns.length > 0) {
            // TODO: Don't make the automatically added primary key columns visible to the user.
            let pk = q.primaryKey().columns
                .filter(each => !(q.columns.some(v => v.name === each.name))) // prevent duplicates.
                .map(each => { return new SingleQueryColumn(each); });
            const [selects, expands] = this.selectExpandString([...q.columns, ...pk]);

            url.searchParams.append("$select", selects);
            if (expands.length > 1) {
                url.searchParams.append("$expand", expands);
            }
        }
    }

    // Implement embedded selects separated by semicolons.
    private selectsFor(columns: QueryColumn[]): string {
        if (0 < columns.length) {
            const [selects, expands] = this.selectExpandString(columns);

            if (expands.length > 1) {
                return "$select=" + selects + ";$expand=" + expands;
            } else {
                return "$select=" + selects
            }
        }
        // No columns? Er...
        return "";
    }

    private selectExpandString(columns: QueryColumn[]): [string, string] {
        let selects: string = "";
        let expands: string = "";
        let firstSelect = true;
        let firstExpand = true;
        for (let each of columns) {
            if (each.isExpanded) {
                // Expanding a column means that the server must also serve that column.
                if (firstExpand) {
                    firstExpand = false;
                } else {
                    expands = expands + ",";
                }
                let subExpand = this.selectsFor(each.columns);
                if (subExpand.length > 1) {
                    expands = expands + `${each.name}(${subExpand};$top=${Table.MAX_EXPANDED_ROWS})`;
                } else {
                    expands = expands + each.name;
                }
            } else {
                if (firstSelect) {
                    firstSelect = false;
                } else {
                    selects = selects + ",";
                }
                selects = selects + each.name;
            }
        }
        return [selects, expands];
    }

    private addFilter: (url: URL, q: Query) => void = (url, q) => {
        let builder: ODataFilterBuilder = new ODataFilterBuilder(url);
        q.filter.visit(builder);
    }

    private addOrderBy: (url: URL, q: Query) => void = (url, q) => {
        if (q.getOrderBy().length > 0) {
            let orderBy = q.getOrderBy()[0].column.name + " ";
            switch (q.getOrderBy()[0].orderedBy) {
                case OrderedBy.ASC:
                    orderBy = orderBy + "asc";
                    break;
                case OrderedBy.DESC:
                    orderBy = orderBy + "desc";
                    break;
                default:
            }
            url.searchParams.append("$orderby", orderBy);
        }
    }

    fetch: (fetcher: Fetcher, q: Query, from: number, to: number) => Promise<Row[]> =
        async (fetcher, q, from, to) => {
            let url = this.rowsUrl(q, from, to);

            return new Promise<Row[]>((resolve, reject) => {
                fetcher.fetchJSON(url).then(
                    json => {
                        try {
                            let result: Array<Row> = [];

                            if (undefined === json.value) {
                                return reject(new Failure("Could not load JSON from server.",
                                    "The data returned by the server does not have a 'value' key and does not appear to be OData data."));
                            }

                            for (let i = 0; i < json.value.length; i++) {
                                let currentRow = json.value[i];
                                this.loadRow(result, currentRow, q);
                            }
                            resolve(result);
                        } catch (e) {
                            reject(e);
                        }
                    },
                    error => {
                        reject(error);
                    }
                )
            });
        }

    private loadRow = (acc: Array<Row>, jsonRow: any, query: MultipleQueryColumn) => {
        if (!jsonRow) {
            console.error(`Server did not pass me an expanded column (${query.name}) when I asked for one.`);
            acc.push(new Row(query));
            return;
        }

        let next: Row = new Row(query);

        for (let eachColumn of query.primaryKey().columns) {
            let value = jsonRow[eachColumn.name];
            next.primaryKeyValues.values.set(eachColumn, value);
        }

        for (let eachColumn of query.columns) {
            if (eachColumn.canHaveChildren()) {
                if (eachColumn.isExpanded) {
                    let rows = jsonRow[eachColumn.name];
                    let c: Cell;
                    if (eachColumn.isCollection) {
                        c = this.loadRows(rows, eachColumn as MultipleQueryColumn);
                    } else {
                        // TODO: This doesn't feel right.
                        c = this.loadRows([rows], eachColumn as MultipleQueryColumn);
                    }
                    next.addCell(c);
                } else {
                    let c : Cell = new Cell();
                    c._type = CellType.UNEXPANDED;
                    c._value = [];
                    next.addCell(c);
                }
            } else {
                // single value.
                let c = new Cell();
                c._type = CellType.SINGLE;
                c._value = jsonRow[eachColumn.name];
                next.addCell(c);
            }
        }
        acc.push(next);
    }

    private loadRows = (jsonRow: any, column: MultipleQueryColumn) => {
        if (!jsonRow) {
            let c = new Cell();
            c._type = CellType.COLLECTION;
            c._value = [];
            return c;
        }

        let acc: Row[] = [];
        for (let i = 0; i < jsonRow.length; i++) {
            let currentRow = jsonRow[i];
            this.loadRow(acc, currentRow, column);
        }
        let c: Cell = new Cell();
        c._type = CellType.COLLECTION;
        c._value = acc;
        return c;
    }

    async fetchCount(fetcher: Fetcher, q: Query): Promise<number> {
        return new Promise<number>((resolve, reject) => {
            let url: URL = this.countUrl(q);
            fetcher.fetchJSON(url).then(
                json => {
                    try {
                        if (undefined === json.value) {
                            return reject(new Failure("Could not load JSON from server.",
                                "The data returned by the server does not have a 'value' key and does not appear to be OData data."));
                        }

                        let count = json["@odata.count"];
                        resolve(parseInt(count));
                    } catch (e) {
                        reject(e);
                    }
                },
                error => {
                    reject(error);
                }
            )
        });
    }
}

class ODataFilterBuilder implements ClauseDispatcher {
    public url: URL;
    public filter: string = "";

    constructor(u: URL) {
        this.url = u;
    }

    // short-hand for "append" text.
    private a(t: string) {
        this.filter = this.filter + t;
    }

    acceptRoot(v: RootClause): void {
        v.clause.visit(this); // Usually this would be a SearchClause.
        if (0 < this.filter.length) {
            this.url.searchParams.append("$filter", this.filter);
        }
    }

    acceptSearch(v: SearchClause): void {
        if (v.isViable()) {
            this.url.searchParams.append("$search", v.value);
        }
    }

    acceptAnd(v: AndClause): void {
        if (!v.left.isViable()) {
            v.right.visit(this);
            return;
        }
        if (!v.right.isViable()) {
            v.left.visit(this);
            return;
        }

        this.a("(");
        v.left.visit(this);
        this.a(" AND ");
        v.right.visit(this);
        this.a(")");
    }

    acceptOr(v: OrClause): void {
        if (!v.left.isViable()) {
            v.right.visit(this);
            return;
        }
        if (!v.right.isViable()) {
            v.left.visit(this);
            return;
        }
        this.a("(");
        v.left.visit(this);
        this.a(" OR ");
        v.right.visit(this);
        this.a(")");
    }

    acceptOperator(v: OperatorClause): void {
        if (v.isViable()) {
            v.left.visit(this);
            switch (v.operator) {
                case OrdOperator.equals:
                    this.a(" eq ");
                    break;
                case OrdOperator.greaterThan:
                    this.a(" gt ");
                    break;
                case OrdOperator.lessThan:
                    this.a(" lt ");
                    break;
            }
            v.right.visit(this);
        }
    }

    acceptColumnChoice(v: ColumnClause): void {
        if (v.column) {
            this.a(v?.asPath());
        }
    }

    acceptLiteral(v: LiteralClause): void {
        switch (v.type) {
            case ColumnType.Date:
                this.a("TODO: dates");
                break;
            case ColumnType.Int32:
                this.a(v.value);
                break;
            case ColumnType.String:
                this.a("'");
                this.a(v.value);
                this.a("'");
                break;
            default:
                this.a("TODO: unimplemented column type.");
                break;
        }
    }
}